// @flow
// $FlowFixMe
import React, { useMemo } from 'react';
import { Grid, Text, AsyncContent, Select, Column } from '@8base/boost';
import { useQuery } from 'react-apollo';
import * as R from 'ramda';
import styled from '@emotion/styled';

import { ENDPOINT_URI } from 'common/constants/apolloOperationContextOptions';
import { GET_WORKSPACE_ENVIRONMENTS_ROLES_QUERY } from 'graphql/queries';
import { getEnvironmentRolesOptions } from 'utils';

type WorkspaceEnvironmentsSelectGroupProps = {
  workspaceId: string,
  workspaceIndex: number,
  workspaceApiHost: string,
  values: any,
  form: any,
  submitting: boolean,
  failedValidationRows: [string],
  setFailedValidationRows: any,
}

const Container = styled.div`
  background: ${props => props.isWorkspaceChosen ? props.theme.COLORS.GRAY_10 : 'none'};
  border-radius: 0 0 5px 5px;
  width: 100%;
  padding: 16px 32px;
`;

const getEnvIndex = (environmentId, environmentRoles) => {
  if (!environmentId || !environmentRoles) {
    return -1;
  }
  return environmentRoles.findIndex((el) => el && el.environmentId === environmentId);
};

const onSelectChange = ({
  environmentId,
  form,
  workspaceIndex,
  value,
  values,
  envRoleIndex,
  workspaceId,
  failedValidationRows,
  setFailedValidationRows,
}) => {
  if (!value) {
    const newEnvRoles = values.workspaceRoles[workspaceIndex].environmentRoles.filter((el) => el.environmentId !== environmentId);

    return form && form.change(`workspaceRoles[${workspaceIndex}].environmentRoles`, newEnvRoles);
  }

  if (!values.workspaceRoles[workspaceIndex]) {
    return form && form.change('workspaceRoles', [...values.workspaceRoles, { workspaceId, environmentRoles: [{ environmentId, roles: value }] }]);
  }

  if (values.workspaceRoles[workspaceIndex] && !values.workspaceRoles[workspaceIndex].environmentRoles[envRoleIndex]) {
    const newFailedRows = failedValidationRows.filter((id) => id !== workspaceId);
    setFailedValidationRows(newFailedRows);

    return form && form.change(
      `workspaceRoles[${workspaceIndex}].environmentRoles`,
      [{ environmentId, roles: value }, ...values.workspaceRoles[workspaceIndex].environmentRoles]);
  }

  return form && form.change(`workspaceRoles[${workspaceIndex}].environmentRoles[${envRoleIndex}].roles`, value);
};

const getFieldValue = ({ values, workspaceIndex, envRoleIndex }) => {
  if (envRoleIndex === -1) {
    return null;
  }

  return values.workspaceRoles[workspaceIndex].environmentRoles[envRoleIndex].roles;
};

export const WorkspaceEnvironmentsSelectGroup = ({
  workspaceId,
  workspaceIndex,
  workspaceApiHost,
  values,
  form,
  submitting,
  failedValidationRows,
  setFailedValidationRows,
}: WorkspaceEnvironmentsSelectGroupProps) => {

  const { data, loading } = useQuery(GET_WORKSPACE_ENVIRONMENTS_ROLES_QUERY, {
    skip: !workspaceId,
    variables: {
      workspaceId,
    },
    context: {
      [ENDPOINT_URI]: workspaceApiHost,
    },
  });

  const isWorkspaceChosen = useMemo(() => workspaceIndex >= 0, [workspaceIndex]);

  const environmentsList = R.pathOr([], ['system', 'getEnvironmentRoles'], data);

  return (
    <Container isWorkspaceChosen={ isWorkspaceChosen }>
      <AsyncContent stretch loading={ loading }>
        <Column>
          { environmentsList.map(({ environmentId, environmentName, roles }) => {
            const roleOptions = getEnvironmentRolesOptions(roles);
            const envRoles = (values.workspaceRoles[workspaceIndex] && values.workspaceRoles[workspaceIndex].environmentRoles) || [];
            const envRoleIndex = getEnvIndex(environmentId, envRoles);
            const fieldValue = getFieldValue({ values, workspaceIndex, envRoleIndex });

            return (
              <Grid.Layout key={ environmentId } columns="160px 1fr" stretch>
                <Grid.Box justifyContent="center">
                  <Text kind="subtitle" color="GRAY_50">{ environmentName }</Text>
                </Grid.Box>
                <Grid.Box>
                  <Select
                    multiple
                    disabled={ submitting }
                    options={ roleOptions }
                    onChange={ (value) => {
                      onSelectChange({
                        workspaceId,
                        workspaceIndex,
                        environmentId,
                        form,
                        values,
                        value,
                        envRoleIndex,
                        failedValidationRows,
                        setFailedValidationRows,
                      });
                    } }
                    value={ fieldValue }
                  />
                </Grid.Box>
              </Grid.Layout>);
          }) }
        </Column>
      </AsyncContent>
    </Container>
  );
};
